import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { ArrowDownAZ, ArrowUpAZ, CalendarRange, Coins, Download, Loader2, Search, SlidersHorizontal, X } from "lucide-react";
import type { TableQueryApi } from "@/hooks/useTableQuery";

interface Props {
  query: TableQueryApi;
  /** Rows currently shown, for the result badge. */
  resultCount?: number;
  onExport?: () => void;
  exporting?: boolean;
  /** Show a spinner in the search box while results are being fetched. */
  searching?: boolean;
  /** Name match hit the account cap, so only the closest matches were used. */
  truncated?: boolean;
  /** Extra controls (bulk actions, refresh) rendered at the end of the bar. */
  children?: React.ReactNode;
  /** Render without the surrounding card, for use inside an existing card. */
  bare?: boolean;
  className?: string;
}

export default function DataToolbar({
  query,
  resultCount,
  onExport,
  exporting,
  searching,
  truncated,
  children,
  bare,
  className,
}: Props) {
  const { spec, state } = query;
  const [rangeOpen, setRangeOpen] = useState(false);
  const [amountOpen, setAmountOpen] = useState(false);

  const dateActive = !!(state.dateFrom || state.dateTo);
  const amountActive = state.amountMin !== "" || state.amountMax !== "";

  const body = (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
      <div className="relative min-w-[200px] flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query.searchInput}
          onChange={(e) => query.setSearchInput(e.target.value)}
          placeholder={spec.searchPlaceholder || "Search by coin, name or email..."}
          className="pl-9 pr-8"
        />
        {searching ? (
          <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        ) : query.searchInput ? (
          <button
            type="button"
            onClick={() => query.setSearchInput("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>


      {(spec.filters ?? []).map((f) => (
        <Select
          key={f.key}
          value={state.filters[f.key] || "all"}
          onValueChange={(v) => query.setFilter(f.key, v)}
        >
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder={f.label} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All {f.label.toLowerCase()}</SelectItem>
            {f.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ))}

      {spec.dateColumn && (
        <Popover open={rangeOpen} onOpenChange={setRangeOpen}>
          <PopoverTrigger asChild>
            <Button variant={dateActive ? "secondary" : "outline"} className="gap-2">
              <CalendarRange className="h-4 w-4" />
              {dateActive ? `${state.dateFrom || "…"} → ${state.dateTo || "…"}` : "Date range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 space-y-3" align="start">
            <div className="space-y-1.5">
              <Label className="text-xs">From</Label>
              <Input type="date" value={state.dateFrom} onChange={(e) => query.setDateFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">To</Label>
              <Input type="date" value={state.dateTo} onChange={(e) => query.setDateTo(e.target.value)} />
            </div>
            {dateActive && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => {
                  query.setDateFrom("");
                  query.setDateTo("");
                }}
              >
                Clear dates
              </Button>
            )}
          </PopoverContent>
        </Popover>
      )}

      {spec.amountColumn && (
        <Popover open={amountOpen} onOpenChange={setAmountOpen}>
          <PopoverTrigger asChild>
            <Button variant={amountActive ? "secondary" : "outline"} className="gap-2">
              <Coins className="h-4 w-4" />
              {amountActive ? `${state.amountMin || "0"} – ${state.amountMax || "∞"}` : "Amount"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-60 space-y-3" align="start">
            <div className="space-y-1.5">
              <Label className="text-xs">Minimum</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={state.amountMin}
                onChange={(e) => query.setAmountMin(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Maximum</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={state.amountMax}
                onChange={(e) => query.setAmountMax(e.target.value)}
                placeholder="Any"
              />
            </div>
            {amountActive && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => {
                  query.setAmountMin("");
                  query.setAmountMax("");
                }}
              >
                Clear amounts
              </Button>
            )}
          </PopoverContent>
        </Popover>
      )}

      {(spec.sorts?.length ?? 0) > 0 && (
        <div className="flex items-center gap-1">
          <Select
            value={state.sortKey || spec.defaultSort?.key || ""}
            onValueChange={(v) => query.setSort(v)}
          >
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {spec.sorts!.map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            aria-label="Toggle sort direction"
            onClick={() => query.setSort(state.sortKey || spec.defaultSort?.key || "", state.sortDirection === "asc" ? "desc" : "asc")}
          >
            {state.sortDirection === "asc" ? <ArrowUpAZ className="h-4 w-4" /> : <ArrowDownAZ className="h-4 w-4" />}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {query.activeCount > 0 && (
          <>
            <Badge variant="secondary" className="text-xs">
              <SlidersHorizontal className="mr-1 h-3 w-3" />
              {query.activeCount} filter{query.activeCount > 1 ? "s" : ""}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={query.reset}
              className="h-8 px-2 text-xs text-muted-foreground"
            >
              <X className="mr-1 h-3 w-3" /> Reset
            </Button>
          </>
        )}
        {typeof resultCount === "number" && (
          <Badge variant="outline" className="text-xs">
            {resultCount.toLocaleString()} result{resultCount !== 1 ? "s" : ""}
          </Badge>
        )}
        {truncated && (
          <span className="text-xs text-muted-foreground">showing the closest matches</span>
        )}

        {onExport && (
          <Button variant="outline" size="sm" onClick={onExport} disabled={exporting} className="gap-1.5">
            <Download className="h-3.5 w-3.5" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        )}
        {children}
      </div>
    </div>
  );

  if (bare) return <div className={className}>{body}</div>;

  return (
    <Card className={className}>
      <CardContent className="p-3">{body}</CardContent>
    </Card>
  );
}
